import React, { Fragment } from "react";
import {useAuth0} from "@auth0/auth0-react";
import {useQueryClient} from "react-query";
import { Icon, makeStyles} from "@material-ui/core";
import { useTripLegs } from "../../../services/trips";
import { ActionButton } from "./controls";
import useAircraftFuelLogsForTripLeg from "../../../services/aircraft/useAircraftFuelLogsForTripLeg";
import TripToolTipStyles from "../TripToolTipStyles";
import { ComboSelect, CurrencyValue } from "../..";
import NumberFormat from "react-number-format";
import useTripLegFuelSuppliers from "../../../services/trips/useTripLegFuelSuppliers";
import FuelOrderIcon from "../../../icons/FuelOrderIcon";
import useColour from "../../../services/settings/useColour";
import moment from "moment";
import SupplierName from "./SupplierName";

const AccessControl = require ("../../../AccessControl");

const FuelScreen = (props) => {
  const {tripData, eventBus} = props;
  const classes = makeStyles(TripToolTipStyles)();

  const { user } = useAuth0();
  const canEditTrip = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Manage TRIPS"]);
  const canEditFuelPolicy = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Manage FUEL ORDERS"]);
  const canEditFuelLog = AccessControl.userHasAccess(user, ["Update FUEL LOGS"]);

  const queryClient = useQueryClient();
  const extraKeys = [tripData.tripUuid, tripData.uuid];
  const tripLeg = useTripLegs({queryClient, extraKeys});
  const fuelKey = [tripData.uuid];
  const tripFuel = useAircraftFuelLogsForTripLeg({queryClient, extraKeys: fuelKey});
  const fuelSuppliers = useTripLegFuelSuppliers({queryClient, extraKeys: fuelKey});
  const status = fuelSuppliers && Array.isArray(fuelSuppliers.data)&& fuelSuppliers.data.length > 0 ?fuelSuppliers.data[0].orderStatus==='CONFIRMED' && props.indicators?.fuel?.status === 'REQUIRED'?'REVIEW':fuelSuppliers.data[0].orderStatus:'REQUIRED';

  const backgroundColor = useColour({code: 'FUEL ORDERS BACKGROUND', status: status||'REQUIRED'});
  const color = useColour({code: 'FUEL ORDERS TEXT', status: status||'REQUIRED'});

  const emptyFuel = {
    "overrideOpening": false,
    "openingFuelKg": 0,
    "openingFuelValue": 0,
    "upliftedFuelDetails": [
      {upliftedKg: 0, upliftedQty: 0, upliftedUom: "LTR", upliftedNote: ""}
    ],
    "upliftFuelUsg": 0,
    "pricePerUsg": 0,
    "upliftedValue": 0,
    "blockFuelKg": 0,
    "upliftedKg": 0,
    "upliftedPricePerKg": 0,
    "blockFuelValue": 0,
    "blockPricePerKg": 0,
    "usedFuelKg": 0,
    "usedFuelValue": 0,
    "remainingFuelKg": 0,
    "remainingFuelValue": 0,
    "confirmedAt": null,
    "confirmedBy": null,
  }

  const fuelData = tripFuel && tripFuel.data ? tripFuel.data : emptyFuel;

  const fuelRows = [
    {label: "Opening", kgField: 'openingFuelKg', valueField: 'openingFuelValue'},
    {label: "Uplifted", kgField: 'upliftedKg', valueField: 'upliftedValue'},
    {label: "Block", kgField: 'blockFuelKg', valueField: 'blockFuelValue'},
    {label: "Used", kgField: 'usedFuelKg', valueField: 'usedFuelValue'},
    {label: "Remaining", kgField: 'remainingFuelKg', valueField: 'remainingFuelValue'},
  ]

  const FuelHeader = (props) => {
    return (
      <div className={classes.fuelHeaderRow}>
        <div style={{fontWeight: "700", display: "inline-block", width: "40%"}}></div>
        <div style={{display: "inline-block", width: "30%", textAlign: "right"}}>Fuel in KG</div>
        <div style={{display: "inline-block", width: "30%", textAlign: "right"}}>Fuel Value</div>
      </div>
    );
  }

  const FuelLine = (props) => {
    return (
      <div className={classes.fuelRow}>
        <div style={{fontWeight: "700", display: "inline-block", width: "40%"}}>{props.label}</div>
        <div style={{display: "inline-block", width: "30%", textAlign: "right"}}>
          <NumberFormat
            value={props.qty}
            thousandSeparator=","
            decimalSeparator="."
            thousandsGroupStyle="thousand"
            decimalScale={0}
            fixedDecimalScale={false}
            type="text"
            displayType="text"
            suffix="kg"
          />
        </div>
        <div style={{display: "inline-block", width: "30%", textAlign: "right"}}>
          <CurrencyValue
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        </div>
      </div>
    );
  }

  return (<div style={{width: "100%", height: "100%", display: "table", borderCollapse:"collapse"}}>
    <div style={{width: "100%", paddingBottom: 5}}>
      <div style={{display: "inline-block"}}><Icon style={{position: "relative", top: 2}}>local_gas_station</Icon></div>
      <div style={{display: "inline-block", textAlign: "left", fontSize: 20, fontWeight: "bold", marginLeft: 3}}>Fuel Details</div>
      <div style={{display: "inline-block", float:"right", textAlign: "right"}}>
        <ActionButton
          variant="outlined"
          startIcon={<Icon>{canEditTrip?"edit":"visibility"}</Icon>}
          onClick={()=>{
            eventBus.trigger("RouteView.TimeWindow.TripAction.editFuel",{tripData: {...tripLeg.data}, canEdit: (canEditTrip || canEditFuelLog)});
          }}
        >
          {(canEditTrip || canEditFuelLog) ?"Edit":"View"}
        </ActionButton>
      </div>
    </div>
    <div style={{width: "100%"}}>
      <FuelHeader/>
      {
        fuelRows.map(row => (
          <FuelLine
            label={row.label}
            qty={fuelData[row.kgField]}
            value={fuelData[row.valueField]}
          />
        ))
      }
    </div>

    <div style={{height: 10}}></div>

    <div style={{display: "table-row", verticalAlign: "bottom", height: "1px"}}>
      <div style={{width: "100%", paddingBottom: 5, marginBottom: 0}}>
        <div style={{display: "inline-block"}}><FuelOrderIcon style={{position: "relative", top: 2}}/></div>
        <div style={{display: "inline-block", textAlign: "left", fontSize: 20, fontWeight: "bold", marginLeft: 3}}>Fuel Order</div>
        <div style={{display: "inline-block", float:"right", textAlign: "right"}}>
          {/* <ActionButton
            variant="outlined"
            startIcon={<Icon>{canEditTrip?"edit":"visibility"}</Icon>}
            onClick={()=>{
              eventBus.trigger("RouteView.TimeWindow.TripAction.editFuelOrder",{tripData: {...tripLeg.data}, canEdit: canEditTrip});
            }}
          >
            {canEditTrip?"Edit":"View"}
          </ActionButton> */}
        </div>
      </div>

      {fuelSuppliers && Array.isArray(fuelSuppliers.data) && fuelSuppliers.data.length > 0 ?
        fuelSuppliers.data[0].type === 'FUEL ORDER'?
          <div style={{width: "calc(100% - 2px)", border: "1px solid #ffffff", fontSize: 14}}>
            <div style={{padding: 5}}>
              <div style={{display: "inline-block"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 60}}>Status:</div>
                <div style={{display: "inline-block", padding: 5, backgroundColor, color}}>
                  <ComboSelect
                    group={fuelSuppliers.data[0].orderStatus==='CONFIRMED' && props.indicators?.fuel?.status === 'REQUIRED'?"FUEL ORDER REVIEW STATUS":"FUEL ORDER STATUS"}
                    subGroup=""
                    value={fuelSuppliers.data[0].orderStatus==='CONFIRMED' && props.indicators?.fuel?.status === 'REQUIRED'?'REVIEW':fuelSuppliers.data[0].orderStatus||'REQUIRED'}
                    displayOnly={true}
                  />
                </div>
              </div>
              <div style={{display: "inline-block", float: "right"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 35}}>Date:</div>
                <div style={{display: "inline-block", padding: "5px 0px", width: 65}}>
                  {fuelSuppliers.data[0].orderDate && fuelSuppliers.data[0].type === 'FUEL ORDER'?moment(fuelSuppliers.data[0].orderDate).format('DD MMM'):''}
                </div>
              </div>
            </div>
            <div style={{padding: "0px 5px 5px 5px"}}>
              <div style={{display: "inline-block"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 60}}>Ref No:</div>
                <div style={{display: "inline-block", padding: "5px 0px", maxWidth: 140, textOverflow:"ellipsis"}}>
                  {fuelSuppliers.data[0].orderReference||''}
                </div>
              </div>
              <div style={{display: "inline-block", float: "right"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 35}}>Qty:</div>
                <div style={{display: "inline-block", padding: "5px 0px", width: 65}}>
                  <NumberFormat
                    value={fuelSuppliers.data[0].orderAmount}
                    thousandSeparator=","
                    decimalSeparator="."
                    thousandsGroupStyle="thousand"
                    decimalScale={0}
                    fixedDecimalScale={false}
                    type="text"
                    displayType="text"
                  />
                </div>
              </div>
            </div>
            <div style={{padding: "0px 5px 5px 5px"}}>
              <div style={{display: "inline-block"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 60}}>Supplier:</div>
                <div style={{display: "inline-block", padding: "5px 0px", maxWidth: 140, textOverflow:"ellipsis"}}>
                  {fuelSuppliers.data[0].supplierName}
                </div>
              </div>
              <div style={{display: "inline-block", float: "right"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 35}}>Rate:</div>
                {fuelSuppliers.data[0].rate?
                  <div style={{display: "inline-block", padding: "5px 0px", width: 65}}>
                    <CurrencyValue value={fuelSuppliers.data[0].rate}/>
                  </div>
                :
                  <div style={{display: "inline-block", padding: "5px 0px", width: 65, backgroundColor: "#ff0000", color: "#ffffff", textAlign:"center"}}>
                    No Rate
                  </div>
                }
              </div>
            </div>
            <div style={{padding: "0px 5px 5px 5px"}}>
              <div style={{display: "block"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 60}}>
                  <div style={{display: "inline-block"}}>
                  <ComboSelect
                    group="FUEL PROVIDER TYPE"
                    subGroup=""
                    value={fuelSuppliers.data[0].fuelProviderType||'ITP'}
                    displayOnly={true}
                  />
                  </div>
                  <div style={{display: "inline-block"}}>:</div>
                </div>
                <div style={{display: "inline-block", padding: "5px 0px", maxWidth: "calc(100% - 70px)", textOverflow:"ellipsis"}}>
                  {fuelSuppliers.data[0].fuelProvider}
                </div>
              </div>
            </div>
            <div style={{padding: "0px 5px 5px 5px"}}>
              <div style={{display: "block"}}>
                <div style={{display: "inline-block", padding: "5px 0px", width: 60, verticalAlign: "top"}}>Policy:</div>
                <div style={{display: "inline-block", marginTop: -4, padding: "5px 0px", maxWidth: "calc(100% - 140px)", textOverflow:"ellipsis"}}>
                  <ComboSelect
                    group="FUEL POLICY TYPE"
                    subGroup=""
                    value={fuelSuppliers.data[0].fuelPolicyType||''}
                    displayOnly={true}
                    style={{display: "inline-block"}}
                  />
                  {fuelSuppliers.data[0].editTankerToLand && fuelSuppliers.data[0].tankerToLand?(<span> {fuelSuppliers.data[0].tankerToLand}<sup style={{paddingLeft: 2}}>t</sup></span>):<sup> </sup>}
                </div>
                {canEditFuelPolicy?
                  <div style={{display: "inline-block", marginTop: -4, float:"right", textAlign: "right"}}>
                    <ActionButton
                      variant="outlined"
                      startIcon={<Icon>edit</Icon>}
                      onClick={()=>{
                        eventBus.trigger("RouteView.TimeWindow.TripAction.editFuelPolicy",{tripData: {...tripLeg.data}, canEdit: canEditFuelPolicy, fuelOrderData: fuelSuppliers.data[0]});
                      }}
                    >
                      Edit
                    </ActionButton>
                  </div>
                :null}
              </div>
            </div>
          </div>
        :
          <>
            <div style={{fontSize: 16, fontWeight: "bold", padding: 10, textAlign: "center", border: "1px solid #ffffff", color: "#000000", backgroundColor: "#dddddd"}}>
              A Fuel Order has not been planned
            </div>
            {fuelSuppliers && fuelSuppliers.data ?
              <>
                <div style={{width: "100%", paddingBottom: 5, marginTop: 15}}>
                  {/* <div style={{display: "inline-block"}}><FuelOrderIcon style={{position: "relative", top: 2}}/></div> */}
                  <div style={{display: "inline-block", textAlign: "left", fontSize: 20, fontWeight: "bold", marginLeft: 3}}>Fuel Suppliers at Airport</div>
                  <div style={{display: "inline-block", float:"right", textAlign: "right"}}>
                    {/* <ActionButton
                      variant="outlined"
                      startIcon={<Icon>{canEditTrip?"edit":"visibility"}</Icon>}
                      onClick={()=>{
                        eventBus.trigger("RouteView.TimeWindow.TripAction.editFuelOrder",{tripData: {...tripLeg.data}, canEdit: canEditTrip});
                      }}
                    >
                      {canEditTrip?"Edit":"View"}
                    </ActionButton> */}
                  </div>
                </div>
                <div style={{border: "1px solid #ffffff", fontSize: 14, padding: "5px 0px 0px 0px"}}>
                {fuelSuppliers.data.map(supplier=> (
                  <div style={{padding: "0px 5px 5px 5px"}}>
                    <div style={{display: "inline-block"}}>
                      <div style={{display: "inline-block", padding: "5px 0px", maxWidth: 140, textOverflow:"ellipsis"}}>
                        <SupplierName companyType="FUEL_SUPPLIER" companyUuid={supplier.supplier}/>
                          {/* {supplier.supplierName} */}
                      </div>
                    </div>
                    <div style={{display: "inline-block", position: "relative", top: -5}}>{supplier.defaultSupplier?<Icon style={{fontSize: "10px", color: "yellow"}}>star</Icon>:null}</div>
                    <div style={{display: "inline-block", float: "right"}}>
                      <div style={{display: "inline-block", padding: "5px 0px", width: 35}}>Rate:</div>
                      {supplier.rate?
                        <div style={{display: "inline-block", padding: "5px 0px", width: 65}}>
                          <CurrencyValue value={supplier.rate}/>
                        </div>
                      :
                        <div style={{display: "inline-block", padding: "5px 0px", width: 65, backgroundColor: "#ff0000", color: "#ffffff", textAlign:"center"}}>
                          No Rate
                        </div>
                      }
                    </div>
                  </div>
                ))}
                </div>
              </>
            : null}
          </>
      : null}
    </div>

  </div>);
}

export default FuelScreen;
